<template>
	<!-- LSP-20406: session tags picked while starting a home quick action. Desktop embeds the same
	     TagComboBox the session About tab uses — outlined field, attached dropdown, type-to-search —
	     in compact-selection mode so the field never grows past 3 chips + "+N". Small screens get a
	     bottom-sheet presentation (mirroring MobileCustomerPicker) because the quick actions render
	     as full-page routes there; the anchor stays compact via SingleLineDisplay. -->
	<div v-test="'session-tag-picker'" class="w-100 d-flex flex-column ga-1">
		<template v-if="useMobileSheet">
			<v-sheet
				v-test="'session-tag-picker-field'"
				border
				rounded="lg"
				class="d-flex align-center ga-1 px-3 py-2 cursor-pointer"
				style="min-height: 48px"
				@click="openSheet">
				<v-icon size="small" class="text-medium-emphasis me-1 flex-shrink-0">icon-shop-tag-stroke</v-icon>
				<!-- The whole field is the picker trigger, so the chip display must be inert:
				     SingleLineDisplay's overflow badge stops clicks and opens its own read-only
				     dialog, which would swallow the tap and stack a second overlay (Bugbot). -->
				<div v-if="modelValue.length > 0" style="pointer-events: none">
					<SingleLineDisplay :model-value="modelValue" :max-visible="3" small bottom-tooltip />
				</div>
				<span v-else class="text-body-2 text-medium-emphasis"><FieldLabel :label="label" /></span>
				<v-spacer />
				<v-icon size="small" class="text-medium-emphasis flex-shrink-0">icon-chevron-down</v-icon>
			</v-sheet>
			<v-bottom-sheet v-model="sheetOpen" inset :z-index="TAG_SHEET_Z_INDEX">
				<v-card v-test="'session-tag-sheet'" rounded="t-xl" class="session-tag-sheet">
					<v-card-title class="d-flex align-center justify-space-between">
						<span>Tags</span>
						<v-btn icon="icon-close-medium" variant="text" aria-label="Close tag picker" @click="sheetOpen = false" />
					</v-card-title>
					<v-card-text class="session-tag-sheet-body">
						<v-text-field
							v-model="searchProxy"
							v-test="'session-tag-sheet-search'"
							label="Search or create a tag"
							density="comfortable"
							clearable
							autofocus
							:messages="isSearchTooLong ? [`New tag names must be ${TAG_MAX_LENGTH} characters or fewer.`] : []"
							v-bind="searchFieldProps(searchProxy)">
						</v-text-field>
						<v-progress-linear v-if="loading" indeterminate color="primary" />
						<v-btn
							v-if="showSheetCreate"
							v-test="'session-tag-sheet-create'"
							block
							variant="text"
							color="primary"
							class="text-none mb-1"
							@click="handleCreateTag">
							Create tag '{{ searchText.trim() }}'
						</v-btn>
						<v-list class="session-tag-sheet-list">
							<v-list-item v-for="tag in filteredTags" :key="tag.id || tag.name" @click="toggleTag(tag)">
								<template #prepend>
									<v-list-item-action start style="pointer-events: none">
										<v-checkbox-btn :model-value="isSelected(tag)" color="primary" />
									</v-list-item-action>
								</template>
								<v-list-item-title>
									<v-chip variant="tonal" :color="tag.baseColor" size="small">{{ tag.name }}</v-chip>
								</v-list-item-title>
							</v-list-item>
							<v-list-item v-if="!loading && filteredTags.length === 0">
								<v-list-item-title>{{ noDataText }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-card-text>
					<v-card-actions class="justify-end px-4 pb-4">
						<PrimaryBtn v-test="'session-tag-sheet-done'" @click="sheetOpen = false">Done</PrimaryBtn>
					</v-card-actions>
				</v-card>
			</v-bottom-sheet>
			<EditTagDialog ref="editTagDialog" />
		</template>
		<TagComboBox
			v-else
			:model-value="modelValue"
			:disabled="disabled"
			:label="label"
			:max-visible-selected="3"
			:can-create="canCreate"
			:load-on-mount="false"
			@update:model-value="emit('update:modelValue', $event)" />
	</div>
</template>

<script setup lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { computed, ref } from "vue";
	import { useDisplay } from "vuetify";
	import api from "@/api";
	import { useCurrentUserRole } from "@/composables/useCurrentUserRole";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import TagComboBox from "@/components/tags/ComboBox.vue";
	import SingleLineDisplay from "@/components/tags/SingleLineDisplay.vue";
	import EditTagDialog from "@/components/tags/EditTagDialog.vue";
	import { TAG_MAX_LENGTH } from "@/api/constants";
	import { countCharacters } from "@/helpers/utilities";
	import type { Label } from "@/types/Label";
	import type ResponseArray from "@/types/ResponseArray";

	// Above the quick-action dialog/page overlays, mirroring MobileCustomerPicker's constant.
	const TAG_SHEET_Z_INDEX = 3100;

	const props = withDefaults(
		defineProps<{
			modelValue: Label[];
			disabled?: boolean;
			label?: string;
		}>(),
		{
			disabled: false,
			label: "Tags (Optional)"
		}
	);

	const emit = defineEmits<{
		(event: "update:modelValue", value: Label[]): void;
	}>();

	type EditTagDialogExpose = { open: (defaultName?: string, defaultColor?: string) => Promise<Label | null> };

	const { smAndDown } = useDisplay();
	const useMobileSheet = computed(() => smAndDown.value);

	const sheetOpen = ref(false);
	const searchText = ref("");
	// Vuetify's clearable writes null into the model; coerce so the trim()/length readers stay safe
	// (same proxy pattern as MobileCustomerPicker).
	const searchProxy = computed({ get: () => searchText.value, set: (value) => (searchText.value = value || "") });
	const loading = ref(false);
	const orgTags = ref<Label[]>([]);
	const noDataText = ref("No tags found.");
	const editTagDialog = ref<EditTagDialogExpose | null>(null);

	// Non-admin roles must not see create affordances that would POST /labels and 403 (LSP-15564) —
	// the same gate EntityAbout applies via its isAdmin prop, resolved here once via the cached-/me
	// role composable so the six quick-action call sites don't each have to thread role state through.
	const { canManageTags: canCreate } = useCurrentUserRole();

	const isSearchTooLong = computed(() => countCharacters(searchText.value) > TAG_MAX_LENGTH);

	const filteredTags = computed(() => {
		if (exceedsSearchLimit(searchText.value)) return [];
		const search = searchText.value.trim().toLowerCase();
		const filtered = search ? orgTags.value.filter((tag) => (tag?.name || "").toLowerCase().includes(search)) : orgTags.value;
		return [...filtered].sort((tagA, tagB) => {
			const aSelected = isSelected(tagA);
			const bSelected = isSelected(tagB);
			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;
			return tagA.name.localeCompare(tagB.name);
		});
	});

	const showSheetCreate = computed(() => {
		// Match the desktop picker: keep the full prefill and validate it in EditTagDialog.
		if (!canCreate.value) return false;
		const term = searchText.value.trim();
		if (!term) return false;
		const lower = term.toLowerCase();
		return !orgTags.value.some((tag) => (tag?.name || "").trim().toLowerCase() === lower);
	});

	function isSelected(tag: Label) {
		return props.modelValue.some((selected) => (selected.id && selected.id === tag.id) || selected.name === tag.name);
	}

	function toggleTag(tag: Label) {
		if (isSelected(tag)) {
			removeTag(tag);
			return;
		}
		emit("update:modelValue", [...props.modelValue, tag]);
	}

	function removeTag(tag: Label) {
		emit(
			"update:modelValue",
			props.modelValue.filter((selected) => !((selected.id && selected.id === tag.id) || selected.name === tag.name))
		);
	}

	async function openSheet() {
		if (props.disabled) return;
		sheetOpen.value = true;
		await loadTags();
	}

	async function loadTags() {
		loading.value = true;
		try {
			const resp = (await (api as any).getLabels({
				page: 1,
				pageSize: 9999,
				where: {},
				order: [["createdAt", "DESC NULLS FIRST"]]
			})) as ResponseArray<Label>;
			orgTags.value = resp.rows;
		} catch (error) {
			console.error("SessionTagPicker: error loading tags", error);
			noDataText.value = "There was an issue loading your organization's tags. Please try again.";
		} finally {
			loading.value = false;
		}
	}

	async function handleCreateTag() {
		const dialog = editTagDialog.value;
		if (!dialog) return;
		const record = await dialog.open(searchText.value.trim());
		if (record) {
			if (!orgTags.value.some((tag) => tag.id === record.id)) {
				orgTags.value = [record, ...orgTags.value];
			}
			if (!isSelected(record)) {
				emit("update:modelValue", [...props.modelValue, record]);
			}
			searchText.value = "";
		}
	}
</script>

<style scoped>
	.session-tag-sheet {
		display: flex;
		flex-direction: column;
		max-height: min(80dvh, 42rem);
		overflow: hidden;
	}

	/* The body itself never scrolls — it is a flex column so the search field and create button
	   stay pinned while ONLY the list below them scrolls (Bugbot: a scrollable body would let
	   the pinned controls scroll away on short viewports). */
	.session-tag-sheet-body {
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	/* Fixed-size, internally-scrolling option list — the same 310px cap Vuetify gives
	   select/autocomplete menus, so the sheet's list matches the other fields' dropdowns.
	   min-height lets it shrink below the cap when the viewport is short. */
	.session-tag-sheet-list {
		max-height: 310px;
		min-height: 0;
		overflow-y: auto;
		overscroll-behavior: contain;
	}
</style>
